import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import type { components } from '../../api/schema';
import { useAuth } from '../../auth/AuthProvider';
import { Badge, Button, Card, Field, Icon, Input, Modal, PageHeader, Select } from '../../design';
import { useToast } from '../../design/Toast';
import s from './roles.module.css';

type Role = components['schemas']['Role'];
type Permission = components['schemas']['Permission'];

export function RolesPage() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => unwrap(api.GET('/roles')) });
  const permissions = useQuery({
    queryKey: ['permissions'],
    queryFn: () => unwrap(api.GET('/permissions')),
  });
  const [editing, setEditing] = useState<Role | 'new' | null>(null);

  const toggle = useMutation({
    mutationFn: ({ role, permission }: { role: Role; permission: Permission }) => {
      const next = role.permissions.includes(permission)
        ? role.permissions.filter((p) => p !== permission)
        : [...role.permissions, permission];
      return unwrap(
        api.PATCH('/roles/{id}', {
          params: { path: { id: role.id } },
          body: { permissions: next },
        }),
      );
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['roles'] }),
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const remove = useMutation({
    mutationFn: (role: Role) =>
      unwrap(api.DELETE('/roles/{id}', { params: { path: { id: role.id } } })),
    onSuccess: () => {
      toast('Rolul a fost șters.', 'success');
      void qc.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });

  const list = roles.data ?? [];
  const perms = permissions.data ?? [];

  return (
    <>
      <PageHeader
        title="Roluri & permisiuni"
        subtitle="Cele 5 roluri de bază sunt fixe; rolurile personalizate primesc un subset de permisiuni și un perimetru."
        actions={
          <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
            Rol personalizat
          </Button>
        }
      />
      <Card tight>
        <div className={s.matrixWrap}>
          <table className={s.matrix}>
            <thead>
              <tr>
                <th>Permisiune</th>
                {list.map((r) => (
                  <th key={r.id}>
                    <div className={s.roleHead}>
                      <span>{r.name}</span>
                      <span className="tiny muted">
                        {r.builtin
                          ? 'built-in'
                          : r.scope === 'all'
                            ? 'personalizat · global'
                            : 'personalizat · organizație'}
                      </span>
                      {!r.builtin && (
                        <button
                          type="button"
                          className={s.roleDelete}
                          aria-label={`Șterge rolul ${r.name}`}
                          onClick={() => remove.mutate(r)}
                        >
                          <Icon name="trash" size={14} />
                        </button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {perms.map((p) => (
                <tr key={p.key}>
                  <td>
                    <strong>{p.label}</strong>
                    <div className="tiny muted mono">{p.key}</div>
                  </td>
                  {list.map((r) => {
                    const on = r.permissions.includes(p.key);
                    const editable = !r.builtin && (me?.orgId === null || r.orgId === me?.orgId);
                    return (
                      <td key={r.id} className={s.cell}>
                        {editable ? (
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => toggle.mutate({ role: r, permission: p.key })}
                            aria-label={`${p.label} pentru ${r.name}`}
                          />
                        ) : on ? (
                          <Icon
                            name="check"
                            size={16}
                            style={{ color: 'var(--green)', margin: '0 auto' }}
                          />
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(roles.isLoading || permissions.isLoading) && (
          <p className="muted" style={{ padding: 16 }}>
            Se încarcă…
          </p>
        )}
        {(roles.error || permissions.error) && (
          <p style={{ padding: 16, color: 'var(--red)' }}>
            {errorMessage(roles.error ?? permissions.error)}
          </p>
        )}
      </Card>
      {editing && <RoleDialog perms={perms} onClose={() => setEditing(null)} />}
    </>
  );
}

function RoleDialog({
  perms,
  onClose,
}: {
  perms: { key: Permission; label: string }[];
  onClose: () => void;
}) {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'all' | 'org'>(me?.orgId ? 'org' : 'all');
  const [selected, setSelected] = useState<Permission[]>([]);
  const create = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/roles', {
          body: {
            name,
            scope,
            orgId: scope === 'org' ? (me?.orgId ?? null) : null,
            permissions: selected,
          },
        }),
      ),
    onSuccess: () => {
      toast('Rolul a fost creat.', 'success');
      void qc.invalidateQueries({ queryKey: ['roles'] });
      onClose();
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  return (
    <Modal
      title="Rol personalizat"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Renunță</Button>
          <Button
            variant="primary"
            onClick={() => create.mutate()}
            busy={create.isPending}
            disabled={name.trim().length < 2 || selected.length === 0}
          >
            Creează
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Nume" htmlFor="role-name">
          <Input
            id="role-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            maxLength={50}
          />
        </Field>
        <Field
          label="Perimetru"
          hint="Un rol global vede toate organizațiile; unul de organizație doar pe a sa."
          htmlFor="role-scope"
        >
          <Select
            id="role-scope"
            value={scope}
            onChange={(e) => setScope(e.target.value as 'all' | 'org')}
            disabled={me?.orgId !== null}
          >
            <option value="all">Global</option>
            <option value="org">Organizație</option>
          </Select>
        </Field>
        <Field label={`Permisiuni (${selected.length})`}>
          <div className={s.permGrid}>
            {perms.map((p) => (
              <label key={p.key} className={s.perm}>
                <input
                  type="checkbox"
                  checked={selected.includes(p.key)}
                  onChange={(e) =>
                    setSelected((xs) =>
                      e.target.checked ? [...xs, p.key] : xs.filter((x) => x !== p.key),
                    )
                  }
                />
                <span>
                  {p.label} <span className="tiny muted mono">{p.key}</span>
                </span>
              </label>
            ))}
          </div>
        </Field>
        {selected.length === 0 && <Badge tone="orange">Alege cel puțin o permisiune</Badge>}
      </div>
    </Modal>
  );
}
